// Trust SaaS commercial-surface data layer — typed wrappers over the G3
// license routes (backend/src/routes/trust.routes.ts). Mirrors backend/src/types/trust.ts
// (hand-kept in lockstep per that file's header; this is the THIRD copy of the
// shape — license backend, license admin-dashboard — both inside this repo).
import { apiRequest } from '@/lib/supabase';

export type SaasLicenseTier = 'free' | 'trust_base' | 'trust_pro' | 'enterprise';
export const SAAS_LICENSE_TIERS: SaasLicenseTier[] = ['free', 'trust_base', 'trust_pro', 'enterprise'];

export type LicenseClass = 'install' | 'platform' | 'saas';
export type TrustUpsellState = 'open' | 'nudged' | 'converted' | 'dismissed';
export type CommercialContactRole = 'admin' | 'billing' | 'content_owner';
export type Ledger = 'saas' | 'platform' | 'install';

export interface LicenseRef {
  id: string;
  license_id: string;
  license_class?: LicenseClass;
  status?: string;
  created_at?: string;
  white_label?: boolean;
  organization?: { id: string; name: string } | null;
}

export interface SaasLicenseDetailsRow {
  id: string;
  license_id: string;
  tier: SaasLicenseTier;
  tprm7_org_id: string | null;
  status: string;
  acquisition_source: string | null;
  stripe_customer_id: string | null;
  created_at: string;
  updated_at: string;
  license?: LicenseRef;
}

// The aggregate bag ingested via POST /telemetry/trust (17_ §6). Aggregates
// only — never content or viewer identities (codex rule 24).
export interface TrustOrgMetrics {
  organization_id: string;
  as_of_date: string;
  teaser_views: number;
  ring1_views: number;
  unique_viewers: number;
  embed_views: number;
  embed_enabled: boolean;
  requests_received: number;
  grants_issued: number;
  parse_consumed: number;
  answers_total: number;
  answers_stale: number;
  single_admin: boolean;
  subprocessor_breach_hits: number;
  anniversary_date?: string | null;
  acquisition_source?: 'share_cta' | 'direct' | 'requested' | 'concierge' | null;
  seeded_from?: 'assessment' | 'claimed' | 'manual' | 'import' | null;
  published: boolean;
  discoverable: boolean;
  verified_domains: number;
  domains_in_grace: number;
  open_disputes: number;

  // GAP-3 (Cost guardrails, D-21) fields, additive within
  // TELEMETRY_TRUST_SCHEMA_VERSION=1. TPRM7's trust-telemetry-rollup sends both
  // on every org row since OF-078 (2026-10-02); a failed usage read sends
  // neither, so a row can still be `undefined`. The Cost guardrails page
  // renders an honest "not yet reported" state instead of fabricating a number.
  storage_mb?: number;
  egress_mb?: number;
}

export interface TrustOrgMetricsRow {
  id: string;
  license_id: string;
  tprm7_org_id: string;
  metric_date: string;
  schema_version: number;
  metrics: TrustOrgMetrics;
  ingested_at: string;
  license?: LicenseRef;
}

export interface TrustUpsellOpportunityRow {
  id: string;
  license_id: string;
  tprm7_org_id: string | null;
  trigger_type: string;
  state: TrustUpsellState;
  value_estimate: number | null;
  converted_sku: string | null;
  opened_at: string;
  nudged_at: string | null;
  converted_at: string | null;
  dismissed_at: string | null;
  created_at: string;
  updated_at: string;
  license?: LicenseRef;
}

export interface CommercialContactRow {
  id: string;
  license_id: string;
  tprm7_org_id: string | null;
  name: string | null;
  email: string;
  role: CommercialContactRole;
  source: string | null;
  synced_at: string | null;
  drift_detected?: boolean;
  created_at: string;
  updated_at: string;
  license?: LicenseRef;
}

// ── Concierge queue (GAP-2, 18H §4.2) — bridge-read trust_center_leads ─────
export type TrustLeadStatus = 'new' | 'contacted' | 'provisioning' | 'provisioned' | 'dismissed';

export interface TrustLead {
  id: string;
  organization_id: string | null;
  email: string;
  company: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_role: string | null;
  source: string | null;
  consent: boolean;
  message: string | null;
  status: TrustLeadStatus;
  resulting_organization_id: string | null;
  created_at: string;
}

// ── Domains & disputes (GAP-4, 18H §4.2) ────────────────────────────────────
export type TrustDisputeResolution = 'upheld' | 'reassigned' | 'dismissed';

export interface NudgeMatrixConfigRow {
  id: string;
  tier: string;
  nudge_type: string;
  enabled: boolean;
  copy_ref: string | null;
  rate_cap_per_period: number | null;
  rate_cap_period: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

// ── Reads ────────────────────────────────────────────────────────────────
export const getSaasDetails = (params?: { licenseId?: string; tier?: string }) => {
  const q = new URLSearchParams();
  if (params?.licenseId) q.set('licenseId', params.licenseId);
  if (params?.tier) q.set('tier', params.tier);
  const qs = q.toString();
  return apiRequest<SaasLicenseDetailsRow[]>(`/trust/saas-details${qs ? `?${qs}` : ''}`);
};

export const getOrgMetrics = (params?: { licenseId?: string }) => {
  const q = new URLSearchParams();
  if (params?.licenseId) q.set('licenseId', params.licenseId);
  const qs = q.toString();
  return apiRequest<TrustOrgMetricsRow[]>(`/trust/org-metrics${qs ? `?${qs}` : ''}`);
};

export const getOpportunities = (params?: { state?: TrustUpsellState; licenseId?: string }) => {
  const q = new URLSearchParams();
  if (params?.state) q.set('state', params.state);
  if (params?.licenseId) q.set('licenseId', params.licenseId);
  const qs = q.toString();
  return apiRequest<TrustUpsellOpportunityRow[]>(`/trust/opportunities${qs ? `?${qs}` : ''}`);
};

// D-53 / OF-168 — the allocation that carries a saas licence's platform trial.
// A free org's trial is not a property of the zero-price saas licence row: it is
// the org's per-company clock on its allocation (license_org_allocations), the
// one Sales > Account already edits. allocation_id addresses
// PATCH /fleet/orgs/:allocationId/trial and /trial/history (accounts.ts).
// `allocation: null` is a NORMAL state with a reason, not an error:
//   no_tprm7_binding - the licence is not bound to a TPRM7 org yet
//   no_hosted_platform_license - this licence server has no hosted platform licence
//                      configured (HOSTED_PLATFORM_LICENSE_ID), so no allocation can be found
//   no_allocation    - bound, but no active allocation yet (a self-serve org gets
//                      one at signup; a concierge-provisioned one at its first phone-home)
export interface SaasTrialAllocation {
  allocation_id: string;
  external_org_id: string;
  org_name: string | null;
  seats: number | null;
  is_trial: boolean;
  trial_expires_at: string | null;
  trial_vendor_limit: number | null;
}
export interface SaasTrialLookup {
  allocation: SaasTrialAllocation | null;
  reason: 'no_tprm7_binding' | 'no_hosted_platform_license' | 'no_allocation' | null;
}
export const getSaasTrial = (licenseId: string) =>
  apiRequest<SaasTrialLookup>(`/trust/saas-trial?licenseId=${encodeURIComponent(licenseId)}`);

export const dismissOpportunity = (id: string) =>
  apiRequest<TrustUpsellOpportunityRow>(`/trust/opportunities/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ state: 'dismissed' }),
  });

export const getContacts = (params?: { role?: CommercialContactRole; licenseId?: string }) => {
  const q = new URLSearchParams();
  if (params?.role) q.set('role', params.role);
  if (params?.licenseId) q.set('licenseId', params.licenseId);
  const qs = q.toString();
  return apiRequest<CommercialContactRow[]>(`/trust/contacts${qs ? `?${qs}` : ''}`);
};

export const pushContacts = (licenseId: string) =>
  apiRequest<{ pushed: boolean }>(`/trust/licenses/${licenseId}/contacts/push`, { method: 'POST' });

export const getLeads = (params?: { status?: TrustLeadStatus; source?: string; limit?: number; cursor?: string }) => {
  const q = new URLSearchParams();
  if (params?.status) q.set('status', params.status);
  if (params?.source) q.set('source', params.source);
  if (params?.limit) q.set('limit', String(params.limit));
  if (params?.cursor) q.set('cursor', params.cursor);
  const qs = q.toString();
  return apiRequest<{ leads: TrustLead[]; nextCursor: string | null }>(`/trust/leads${qs ? `?${qs}` : ''}`);
};

export const claimLead = (id: string) => apiRequest<TrustLead>(`/trust/leads/${id}/claim`, { method: 'POST' });
export const dismissLead = (id: string) => apiRequest<TrustLead>(`/trust/leads/${id}/dismiss`, { method: 'POST' });

export const getNudgeMatrix = () => apiRequest<NudgeMatrixConfigRow[]>('/trust/nudge-matrix');

export const upsertNudgeMatrixRow = (row: {
  tier: string; nudge_type: string; enabled: boolean; copy_ref?: string | null;
  rate_cap_per_period?: number | null; rate_cap_period?: 'day' | 'week' | 'month' | null;
}) => apiRequest<NudgeMatrixConfigRow>('/trust/nudge-matrix', { method: 'POST', body: JSON.stringify(row) });

export const runLifecycle = () =>
  apiRequest<{ orgsEvaluated: number; opportunitiesOpened: number; opportunitiesNudged: number; suppressedBundled: number; rateCapped: number; heldForBridge?: number }>(
    '/trust/lifecycle/run', { method: 'POST' }
  );

export const provisionSaasOrg = (body: Record<string, unknown>) =>
  apiRequest<{ license: { id: string; license_id: string }; saasDetails: SaasLicenseDetailsRow; bridge: unknown }>(
    '/trust/provision', { method: 'POST', body: JSON.stringify(body) }
  );

export const adminResetInitiate = (licenseId: string, adminEmailOverride?: string) =>
  apiRequest<{ status: string; audit_id?: string }>(`/trust/licenses/${licenseId}/admin-reset-initiate`, {
    method: 'POST',
    body: adminEmailOverride ? JSON.stringify({ adminEmailOverride }) : undefined,
  });

// GAP-4 dispute-resolve bridge action (18G §2.4). Wired end-to-end on this
// side; succeeds once TPRM7 ships the `platform-trust-domains` counterpart
// (backend/src/utils/tprm7-bridge.ts) — until then it throws a normal
// ApiError the caller surfaces as a toast, never a silent no-op.
export const resolveDomainDispute = (
  licenseId: string,
  body: { domain: string; resolution: TrustDisputeResolution; note?: string | null }
) =>
  apiRequest<{ organization_id: string; domain: string; status: string }>(
    `/trust/licenses/${licenseId}/disputes/resolve`,
    { method: 'POST', body: JSON.stringify(body) }
  );

// ── Derived helpers (shared display logic across the console pages) ───────

export const NUDGE_TYPE_LABEL: Record<string, string> = {
  single_admin: 'Single admin risk',
  staleness_digest: 'Staleness digest',
  anniversary: 'Anniversary',
  access_review: 'Access review',
  trust_pro_upsell: 'Trust Pro upsell',
  domain_grace: 'Domain grace expiring',
  subprocessor_breach: 'Subprocessor breach hit',
  trial_expiry: 'Platform trial expiring',
};

export function nudgeTypeLabel(t: string): string {
  return NUDGE_TYPE_LABEL[t] || t.replace(/_/g, ' ');
}

// trust_center_leads.source real-world values (18A §6.5 / migration 20260716000000):
// 'cta' = the D-45 assessment-completion CTA (a vendor mid-assessment is
// offered a Trust Center — "seeded" off that session); 'join_request' =
// a vendor asking to join an already-claimed profile (D-25 join-first);
// 'directory_invite' = a buyer directly inviting an unclaimed company via the
// directory (D-31). Chip labels per 18H §4.2 "seeded/requested/direct".
export const LEAD_SOURCE_LABEL: Record<string, string> = {
  cta: 'Seeded',
  join_request: 'Requested',
  directory_invite: 'Direct',
};

export function leadSourceLabel(source: string | null): string {
  if (!source) return 'Unknown';
  return LEAD_SOURCE_LABEL[source] || source.replace(/_/g, ' ');
}

export function tierLabel(tier: string): string {
  return tier === 'trust_base' ? 'Trust Base' : tier === 'trust_pro' ? 'Trust Pro' : tier.charAt(0).toUpperCase() + tier.slice(1);
}

export function orgNameFor(row: { license?: LicenseRef }): string {
  return row.license?.organization?.name || row.license?.license_id || '—';
}
